import { useTracker } from "@newsela/snitch";
import { NextRouter } from "next/router";
import { DispatchWithoutAction, useRef } from "react";

import useAssignmentCreate from "@/assignments/useAssignmentCreate";
import {
  handleHasUnsavedChanges,
  handleProvideConsent,
  handleSetAssignment,
  verifyAssigneeSelection,
} from "@/assignments/utils/assignmentActions";
import { getLiteContentWithEarliestExpireDate } from "@/assignments/utils/assignmentUtils";
import { formatAssignmentForReducer } from "@/assignments/utils/formatAssignmentForReducer";
import { formatAssignmentPayload } from "@/assignments/utils/formatAssignmentPayload";
import { usePublishAssignment } from "@/assignments/utils/usePublishAssignment/usePublishAssignment";
import useSaveDraftAssignment from "@/assignments/utils/useSaveDraftAssignment";
import globalToasterRef from "@/base/globalToasterRef";
import { getLicenseTypeFromList } from "@/onboarding/onboarding.utils";
import { Assignment } from "@/types/models";
import useEventRecordMutation from "@/users/useEventRecordMutation/useEventRecordMutation";
import { UseGetUserPropertiesReturn } from "@/users/v3/useGetUserProperties";
import useUserFeaturesQuery from "@/users/v3/useUserFeaturesQuery";
import { FIRST_ASSIGNMENT_COMPLETED_EVENT } from "@/view/AssignActionsContainer/AssignActionsContainer";
import {
  HandleSave,
  ISavingState,
} from "@/view/AssignActionsContainer/AssignActionsContainer.interface";
import handleSaveConfirmation from "@/view/utils/handleSaveConfirmation";
import updateUrlAfterSave from "@/view/utils/updateUrlAfterSave";
import type { IUnlevelledContent } from "@/types/models/UnlevelledContent.interface";

interface IUseGetAssignmentHandlers {
  content: IUnlevelledContent;
  dispatch: DispatchWithoutAction;
  fetchAssignment: Function;
  router: NextRouter;
  selectedAssignment: {
    assignment_id: number;
  };
  setAdvisoryModalActive: (isActive: boolean) => void;
  setIsAssignModalActive: (isActive: boolean) => void;
  setSavingState: (savingState: ISavingState) => void;
  setShareModalActive: (isActive: boolean) => void;
  updatedAssignment: Assignment;
  userProperties: Partial<UseGetUserPropertiesReturn>;
}

export default function useGetAssignmentHandlers({
  content,
  dispatch,
  fetchAssignment,
  router,
  selectedAssignment,
  setAdvisoryModalActive,
  setIsAssignModalActive,
  setSavingState,
  setShareModalActive,
  updatedAssignment,
  userProperties,
}: IUseGetAssignmentHandlers) {
  const { data: userFeatures } = useUserFeaturesQuery();
  const [createAssignment] = useAssignmentCreate();
  const { trackAction } = useTracker();
  const { postEventRecord } = useEventRecordMutation();

  const initialAssignmentRef = useRef<Assignment | null>(null);
  const handleSetInitialAssignmentRef = (updatedAssignment: Assignment) => {
    initialAssignmentRef.current = updatedAssignment;
  };

  const liteContent = getLiteContentWithEarliestExpireDate([content.data]);
  const maxScheduleDate =
    userProperties?.isLiteUser && liteContent.date_lite_product_grant_expires;
  const initialSavingState = { type: null, value: false };

  const handleSave = async ({
    eventProperties,
    isDraft,
    saveErrorToastProps,
    saveSuccessToastProps,
  }: HandleSave) => {
    setSavingState({ type: isDraft ? "draft" : "publish", value: true });
    // Don't create non-draft assignments past the maxScheduleDate
    if (
      !isDraft &&
      Boolean(maxScheduleDate) &&
      (updatedAssignment?.date_goes_live !== null) > maxScheduleDate
    ) {
      setSavingState(initialSavingState);
      return;
    }

    const formattedAssignment = formatAssignmentPayload(
      updatedAssignment || {},
      isDraft,
      selectedAssignment,
    );

    const response = await createAssignment(formattedAssignment);

    if (response.error) {
      setSavingState(initialSavingState);
      return globalToasterRef.current.displayToast(saveErrorToastProps);
    }

    setSavingState(initialSavingState);
    setIsAssignModalActive(false);
    const assignmentForReducer = formatAssignmentForReducer(
      response.data,
      false,
    );
    handleSetAssignment(assignmentForReducer, dispatch);
    updateUrlAfterSave({
      response,
      router,
      selectedAssignment,
    });
    // update the reference assignment for form value changes
    handleSetInitialAssignmentRef(updatedAssignment);
    handleHasUnsavedChanges(false, dispatch);
    handleSaveConfirmation({
      saveSuccessToastProps,
      isDraft,
      currentAssignment: updatedAssignment,
      setShareModalActive,
    });
    // We only want to track this event if save is successful
    trackAction(eventProperties);
    fetchAssignment({ id: response?.data?.content_id });
    // Add record to confirm the user's first assignment is complete.
    if (userFeatures?.includes("needs_first_assignment")) {
      const licenseType = getLicenseTypeFromList(userFeatures);
      postEventRecord({
        event_name: FIRST_ASSIGNMENT_COMPLETED_EVENT,
        data: {
          license_type: licenseType,
        },
      });
    }
    return response;
  };

  const { handlePublish } = usePublishAssignment({
    currentContentLevelId: content.id,
    handleProvideConsent,
    handleSave,
    isDisabled: false,
    setAdvisoryModalActive,
    updatedAssignment,
    verifyAssigneeSelection: () =>
      verifyAssigneeSelection(updatedAssignment, dispatch),
    maxScheduleDate,
  });

  const { handleSaveDraft } = useSaveDraftAssignment({
    currentContentLevelId: content.id,
    handleSave,
    updatedAssignment,
  });

  return { handleSaveDraft, handleSave, handlePublish };
}
